/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import MUIDataTable from "mui-datatables";
import { toast } from "react-toastify";
import { getData } from "../../Services/getData";
import { UrlEnum, LocalUrlEnum } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import OrderShipping, { OrderShippingState } from "../Orders/OrderShipping";

export default function MultiOrder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [multiOrder, setMultiOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [localShipping, setLocalShipping] = useState<OrderShippingState | null>(null);

  useEffect(() => {
    if (id) {
      fetchMultiOrder(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function fetchMultiOrder(multiOrderId: string) {
    setLoading(true);
    getData(`${UrlEnum.multiOrders}/${multiOrderId}`)
      .then((res) => {
        setMultiOrder(res.data);
        setLocalShipping(res.data?.orderIds?.[0]?.shipping || res.data?.childOrders?.[0]?.shipping || null);
        setLoading(false);
      })
      .catch(() => {
        toast.error(Vocabulary.getError);
        setLoading(false);
      });
  }

  const childOrders: any[] = multiOrder?.childOrders || multiOrder?.orderIds || [];
  const firstChild = childOrders[0] || {};
  const customer = firstChild?.customer;
  const personalData = customer?.personalData;
  const awbFiles: any[] = multiOrder?.awbFiles || [];
  const latestAwb = awbFiles.length > 0 ? awbFiles[awbFiles.length - 1] : null;

  const childOrderColumns = [
    {
      label: Vocabulary.number,
      name: "orderNumber",
      options: {
        customBodyRender: (_: any, tableMeta: any) => {
          const child = childOrders[tableMeta.rowIndex];
          return (
            <Typography>{child?.orderNumber || "-"}</Typography>
          );
        },
      },
    },
    {
      label: Vocabulary.deviceName,
      name: "deviceDetails",
      options: {
        customBodyRender: (_: any, tableMeta: any) => {
          const child = childOrders[tableMeta.rowIndex];
          return (
            <Typography>{child?.deviceDetails?.deviceName || "-"}</Typography>
          );
        },
      },
    },
    {
      label: Vocabulary.status,
      name: "status",
      options: {
        customBodyRender: (_: any, tableMeta: any) => {
          const child = childOrders[tableMeta.rowIndex];
          return <Typography>{child?.status?.order || "-"}</Typography>;
        },
      },
    },
    {
      label: Vocabulary.estimatedPrice,
      name: "estimatedPrice",
      options: {
        customBodyRender: (_: any, tableMeta: any) => {
          const child = childOrders[tableMeta.rowIndex];
          return (
            <Typography>
              {child?.estimatedPrice != null
                ? `${child.estimatedPrice} lei`
                : "-"}
            </Typography>
          );
        },
      },
    },
    {
      label: "",
      name: "actions",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex: number) => {
          const child = childOrders[dataIndex];
          return (
            <IconButton
              size="small"
              onClick={() => navigate(`${LocalUrlEnum.orders}${child?._id}`)}
            >
              <Edit fontSize="small" />
            </IconButton>
          );
        },
      },
    },
  ];

  const childTableOptions = {
    serverSide: false,
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    selectableRows: "none" as any,
    pagination: false,
    textLabels: {
      body: {
        noMatch: Vocabulary.noResultsFound,
      },
    },
  };

  function InfoRow({
    label,
    value,
  }: {
    label: string;
    value: string | undefined | null;
  }) {
    return (
      <Grid item xs={12} sm={6}>
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1">{value || "-"}</Typography>
      </Grid>
    );
  }

  return (
    <>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress color="primary" />
        </div>
      ) : (
        <Grid container spacing={2}>
          {/* Section 1 — Child orders table */}
          <Grid item xs={12}>
            <MUIDataTable
              title={Vocabulary.childOrders}
              data={childOrders}
              columns={childOrderColumns}
              options={childTableOptions}
            />
          </Grid>

          {/* Section 2 — Customer info */}
          <Grid item xs={12} md={6}>
            <Paper style={{ padding: 16 }}>
              <Typography variant="h6" gutterBottom>
                {Vocabulary.clientInfo}
              </Typography>
              <Grid container spacing={1}>
                <InfoRow label={Vocabulary.name} value={customer?.name} />
                <InfoRow label={Vocabulary.email} value={customer?.email} />
                <InfoRow
                  label={Vocabulary.phone}
                  value={personalData?.phoneNumber}
                />
                <InfoRow label={Vocabulary.iban} value={personalData?.iban} />
                <InfoRow
                  label={Vocabulary.accountFullName}
                  value={personalData?.accountFullName}
                />
                <InfoRow
                  label={Vocabulary.seriesAndNumber}
                  value={personalData?.seriesAndNumber}
                />
                <InfoRow label={Vocabulary.note} value={personalData?.note} />
              </Grid>
            </Paper>
          </Grid>

          {/* Section 3 — Shipping + Fan Courier (same component as normal orders) */}
          <Grid item xs={12}>
            <OrderShipping
              shipping={localShipping || firstChild?.shipping}
              fanCourierInfo={latestAwb}
              order={multiOrder}
              handleChangeOrder={setMultiOrder}
              handleChangeShipping={(newShipping: OrderShippingState) =>
                setLocalShipping(newShipping)
              }
              saveOrder={() => Promise.resolve()}
              fanCourierOverrides={{
                generateAWBUrl: `${UrlEnum.multiOrders}/generateAWB/${id}`,
                buildDeleteAWBUrl: (awb: string) =>
                  `${UrlEnum.multiOrders}/deleteAWB/${id}/${awb}`,
                createFanOrderUrl: latestAwb?.awb
                  ? `${UrlEnum.multiOrders}/createFanOrder/${id}/${latestAwb.awb}`
                  : null,
                buildDeleteOrderUrl: (awb: string) =>
                  `${UrlEnum.multiOrders}/deleteOrder/${id}/${awb}`,
              }}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
}
